import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import {
    getClassification,
    payloadToObject,
    severityBadgeClass
} from "./NotificationMonitoring";

function NotificationDetailsDrawer({ item, guide }) {

    const [payloadView, setPayloadView] = useState("json");
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        setExpandedSections({});
    }, [item]);

    if (!item) {
        return null;
    }

    const classification = getClassification(
        item.type,
        guide
    );

    const payloadObj = payloadToObject(item.payload);
    const entries = Object.entries(payloadObj);

    /* =====================================================
       Cascading Payload Logic
    ===================================================== */

    const topLevelSections = {
        ANY_OFFER_CHANGED: [
            /^Offers\s+\d+$/i,
            /^Summary$/i,
            /^Offer Change Trigger$/i
        ],

        B2B_ANY_OFFER_CHANGED: [
            /^Offers\s+\d+$/i,
            /^Summary$/i,
            /^Offer Change Trigger$/i
        ],

        FBA_INVENTORY_AVAILABILITY_CHANGES: [
            /^Fulfillment Inventory By Marketplace\s+\d+$/i
        ],

        ORDER_CHANGE: [
            /^Summary$/i,
            /^Order Change Trigger$/i
        ],

        PRICING_HEALTH: [
            /^Summary$/i,
            /^Merchant Offer$/i,
            /^Offer Change Trigger$/i
        ],

        ACCOUNT_STATUS_CHANGED: [
            /^Account Status Change Notification$/i
        ]
    };

    const isTopLevelSection = (label) => {
        const patterns =
            topLevelSections[item.type] || [];

        return patterns.some((pattern) =>
            pattern.test(String(label))
        );
    };

    const toggleSection = (key) => {
        setExpandedSections((prev) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const isExpanded = (key) => {
        return expandedSections[key] === true;
    };

    const Arrow = ({ open }) => (
        <span
            className={`anm-payload-arrow ${
                open
                    ? "anm-arrow-open"
                    : "anm-arrow-closed"
            }`}
        />
    );

    const SectionHeader = ({
        label,
        sectionKey
    }) => {

        const open =
            isExpanded(sectionKey);

        return (
            <div
                className="anm-payload-section-header"
                onClick={() =>
                    toggleSection(sectionKey)
                }
            >
                <span>
                    {label}
                </span>

                <Arrow open={open} />
            </div>
        );
    };

    const ChildHeader = ({ label }) => (
        <div className="anm-payload-child-header">
            {label}
        </div>
    );

    const ValueRow = ({ item }) => {

        if (
            item.isHeader &&
            item.label
        ) {
            return (
                <ChildHeader
                    label={item.label}
                />
            );
        }

        if (
            !item.label ||
            item.label === ":"
        ) {
            return (
                <div className="anm-payload-value-row">
                    {item.value != null &&
                    item.value !== ""
                        ? String(item.value)
                        : "-"}
                </div>
            );
        }

        return (
            <div className="anm-payload-value-row">

                <span className="anm-payload-field-label">
                    {item.label}
                </span>

                <span className="anm-payload-field-separator">
                    :
                </span>

                <span>
                    {item.value != null &&
                    item.value !== ""
                        ? String(item.value)
                        : "-"}
                </span>

            </div>
        );
    };

    const createSections = () => {

        const sections = [];
        let currentSection = null;

        (Array.isArray(item.payload)
            ? item.payload
            : []
        ).forEach((payloadItem) => {

            if (
                payloadItem.isHeader &&
                isTopLevelSection(
                    payloadItem.label
                )
            ) {

                currentSection = {
                    label: payloadItem.label,
                    items: []
                };

                sections.push(
                    currentSection
                );

            } else if (currentSection) {

                currentSection.items.push(
                    payloadItem
                );

            } else {

                sections.push({
                    label: null,
                    items: [payloadItem]
                });

                currentSection =
                    sections[
                        sections.length - 1
                    ];
            }
        });

        return sections;
    };

    const renderSection = (
        section,
        index
    ) => {

        if (!section.label) {

            return section.items.map(
                (payloadItem, itemIndex) => (
                    <ValueRow
                        key={itemIndex}
                        item={payloadItem}
                    />
                )
            );
        }

        const sectionKey =
            `section-${index}`;

        const open =
            isExpanded(sectionKey);

        return (
            <div
                key={sectionKey}
                className="anm-payload-section"
            >

                <SectionHeader
                    label={section.label}
                    sectionKey={sectionKey}
                />

                {open && (
                    <div className="anm-payload-section-content">

                        {section.items.map(
                            (payloadItem, itemIndex) => (
                                <ValueRow
                                    key={itemIndex}
                                    item={payloadItem}
                                />
                            )
                        )}

                    </div>
                )}

            </div>
        );
    };

    const sections = createSections();

    return (
        <div className="anm-details">

            {/* =================================================
                Notification Type
            ================================================= */}

            <div className="anm-detail-section">

                <div className="anm-detail-label">
                    Notification Type
                </div>

                <div className="anm-details-title">
                    {item.type || "-"}
                </div>

            </div>


            {/* =================================================
                Store
            ================================================= */}

            <div className="anm-detail-section">

                <div className="anm-detail-label">
                    Store
                </div>

                <div className="anm-store-name">
                    {item.store || "-"}
                </div>

            </div>


            {/* =================================================
                Identifiers
            ================================================= */}

            <Row className="anm-identifier-row">

                <Col md={4}>

                    <div className="anm-info-box">

                        <div className="anm-detail-label">
                            Seller ID
                        </div>

                        <div className="anm-info-value anm-monospace">
                            {item.sellerId || "N/A"}
                        </div>

                    </div>

                </Col>


                <Col md={4}>

                    <div className="anm-info-box">

                        <div className="anm-detail-label">
                            ASIN
                        </div>

                        <div className="anm-info-value">
                            {item.asin || "N/A"}
                        </div>

                    </div>

                </Col>


                <Col md={4}>

                    <div className="anm-info-box">

                        <div className="anm-detail-label">
                            SKU
                        </div>

                        <div className="anm-info-value">
                            {item.sku || "N/A"}
                        </div>

                    </div>

                </Col>

            </Row>


            {/* =================================================
                Classification
            ================================================= */}

            <div className="anm-detail-section">

                <div className="anm-detail-label">
                    Classification
                </div>

                <div className="anm-classification">

                    <div className="anm-classification-item">

                        <span className="anm-classification-label">
                            Severity
                        </span>

                        <span
                            className={`anm-badge ${severityBadgeClass(
                                classification.severity
                            )}`}
                        >
                            {classification.severity}
                        </span>

                    </div>


                    <div className="anm-classification-item">

                        <span className="anm-classification-label">
                            Business Impact
                        </span>

                        <strong>
                            {classification.businessImpact}
                        </strong>

                    </div>


                    <div className="anm-classification-item">

                        <span className="anm-classification-label">
                            Attention
                        </span>

                        <span
                            className={`anm-badge ${
                                classification.needsAttention ===
                                "Yes"
                                    ? "anm-badge-attention-yes"
                                    : "anm-badge-attention-no"
                            }`}
                        >
                            {classification.needsAttention}
                        </span>

                    </div>

                </div>

            </div>


            {/* =================================================
                Recommended Action
            ================================================= */}

            <div className="anm-detail-section">

                <div className="anm-detail-label">
                    Recommended Action
                </div>

                <div className="anm-recommended-action-box">
                    {classification.recommendedAction}
                </div>

            </div>


            {/* =================================================
                Payload
            ================================================= */}

            <div className="anm-detail-section anm-payload-section-wrapper">

                <div className="anm-payload-header">

                    <div className="anm-detail-label mb-0">
                        Notification Payload
                    </div>


                    <div className="anm-view-toggle">

                        <button
                            type="button"
                            className={
                                payloadView === "json"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setPayloadView("json")
                            }
                        >
                            JSON
                        </button>


                        <button
                            type="button"
                            className={
                                payloadView === "data"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setPayloadView("data")
                            }
                        >
                            Data View
                        </button>

                    </div>

                </div>


                {/* =================================================
                    JSON VIEW
                ================================================= */}

                {payloadView === "json" && (

                    <pre className="anm-payload-pre">
                        {JSON.stringify(
                            payloadObj,
                            null,
                            2
                        )}
                    </pre>

                )}


                {/* =================================================
                    DATA VIEW
                ================================================= */}

                {payloadView === "data" && (

                    <div className="anm-payload-cascade">

                        {sections.length === 0 ? (

                            <div className="anm-no-payload">
                                No payload fields
                            </div>

                        ) : (

                            sections.map(
                                (section, index) =>
                                    renderSection(
                                        section,
                                        index
                                    )
                            )

                        )}

                    </div>

                )}

            </div>

        </div>
    );
}

export default NotificationDetailsDrawer;